/**
 * Inline rich-text markup for admin-authored text.
 *
 *     Make it [b]bold[/b] and [c=#00ffff]bright[/c]
 *
 * Four tags — `[b]`, `[i]`, `[u]` and `[c=#hex]` — which may nest freely.
 * Accepted on home layout **section titles**, where `RichTextField` edits them
 * as formatted text: nobody types a tag, and the markup only exists either side
 * of the editor. The DOM helpers at the bottom are what bridge the two.
 *
 * Mirrors `Backend/src/utils/richText.ts` and `MobileApp/utils/richText.ts`
 * (services never share code) — the backend re-validates everything on save.
 */

export interface TextRun {
  text: string;
  /** Lowercased hex, or undefined for the app's default text color. */
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

/** `[b]`/`[i]`/`[u]`, their closers, `[c=…]`, or `[/c]`. */
const TAG_PATTERN = /\[(b|i|u)\]|\[\/(b|i|u)\]|\[c=([^\]]*)\]|\[\/c\]/g;
const HEX_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const hasRichText = (text: string): boolean => text.includes('[');
export const isColorValue = (value: string): boolean => HEX_PATTERN.test(value.trim());

/** One entry of the open-tag stack: a style mark, or a color. */
type Mark = 'b' | 'i' | 'u' | { color: string };

const marksToRun = (stack: Mark[], text: string): TextRun => {
  const run: TextRun = { text };
  if (stack.includes('b')) run.bold = true;
  if (stack.includes('i')) run.italic = true;
  if (stack.includes('u')) run.underline = true;
  for (let i = stack.length - 1; i >= 0; i--) {
    const mark = stack[i];
    if (typeof mark !== 'string') {
      run.color = mark.color;
      break;
    }
  }
  return run;
};

const sameMarks = (a: TextRun, b: TextRun): boolean =>
  a.color === b.color && !!a.bold === !!b.bold && !!a.italic === !!b.italic && !!a.underline === !!b.underline;

/** Splits text into styled runs, ignoring anything malformed. */
export const parseRuns = (text: string): TextRun[] => {
  const runs: TextRun[] = [];
  const stack: Mark[] = [];
  let cursor = 0;

  const push = (chunk: string) => {
    if (!chunk) return;
    const run = marksToRun(stack, chunk);
    const previous = runs[runs.length - 1];
    if (previous && sameMarks(previous, run)) {
      previous.text += chunk;
      return;
    }
    runs.push(run);
  };

  const pop = (predicate: (mark: Mark) => boolean) => {
    for (let i = stack.length - 1; i >= 0; i--) {
      if (predicate(stack[i])) {
        stack.splice(i, 1);
        return;
      }
    }
  };

  TAG_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TAG_PATTERN.exec(text)) !== null) {
    push(text.slice(cursor, match.index));
    cursor = TAG_PATTERN.lastIndex;
    const [, open, close, color] = match;
    if (open) stack.push(open as Mark);
    else if (close) pop((mark) => mark === close);
    else if (color !== undefined) {
      if (isColorValue(color)) stack.push({ color: color.trim().toLowerCase() });
    } else pop((mark) => typeof mark !== 'string');
  }
  push(text.slice(cursor));
  return runs;
};

/** A run's tags, outermost first — the canonical order every writer emits. */
const tagsOf = (run: TextRun): string[] => [
  ...(run.bold ? ['b'] : []),
  ...(run.italic ? ['i'] : []),
  ...(run.underline ? ['u'] : []),
  ...(run.color ? [`c=${run.color}`] : []),
];

const closerFor = (tag: string): string => (tag.startsWith('c=') ? '[/c]' : `[/${tag}]`);

/** Renders runs back to markup, keeping tags open across runs that share them. */
export const serializeRuns = (runs: TextRun[]): string => {
  let out = '';
  let open: string[] = [];

  for (const run of runs) {
    if (!run.text) continue;
    const tags = tagsOf(run);
    let shared = 0;
    while (shared < open.length && shared < tags.length && open[shared] === tags[shared]) shared++;
    for (let i = open.length - 1; i >= shared; i--) out += closerFor(open[i]);
    for (let i = shared; i < tags.length; i++) out += `[${tags[i]}]`;
    out += run.text;
    open = tags;
  }

  for (let i = open.length - 1; i >= 0; i--) out += closerFor(open[i]);
  return out;
};

/** The text as a user reads it in the app — every tag removed. */
export const stripRichText = (text: string): string =>
  hasRichText(text) ? parseRuns(text).map((run) => run.text).join('') : text;

/** Visible length: what the character cap is measured against. */
export const visibleLength = (text: string): number => stripRichText(text).length;

/** Rewrites markup into its canonical, balanced form (what the backend stores). */
export const normalizeRichText = (text: string): string => serializeRuns(parseRuns(text));

/** Returns an error message for malformed markup, or null. Mirrors the backend's rules. */
export const richTextError = (text: string, slot: string): string | null => {
  const open: string[] = [];
  TAG_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TAG_PATTERN.exec(text)) !== null) {
    const [tag, openTag, closeTag, color] = match;
    if (openTag) {
      open.push(openTag);
      continue;
    }
    if (color !== undefined) {
      if (!isColorValue(color)) return `${slot}: "${color}" is not a color — use a hex value like #00ffff`;
      open.push('c');
      continue;
    }
    const expected = closeTag || 'c';
    if (open.length === 0) return `${slot}: ${tag} without a matching opening tag before it`;
    if (open[open.length - 1] !== expected) {
      return `${slot}: ${tag} closes out of order — [${open[open.length - 1]}] is still open`;
    }
    open.pop();
  }
  if (open.length > 0) return `${slot}: [${open[open.length - 1]}] is never closed`;
  return null;
};

/* ---------------------------------------------------------------------------
   Markup ↔ DOM. The editor is a contentEditable showing formatted text, so
   these are the only two places raw markup is handled in the browser.
   --------------------------------------------------------------------------- */

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Markup → HTML for the editable field. Semantic `<b>/<i>/<u>` (rather than
 * styled spans) are what `document.queryCommandState` reads to light the
 * toolbar buttons up, and what execCommand toggles cleanly.
 */
export const markupToHtml = (markup: string): string =>
  parseRuns(markup)
    .map((run) => {
      let html = escapeHtml(run.text).replace(/ {2}/g, ' &nbsp;');
      if (run.color) html = `<span style="color: ${run.color}">${html}</span>`;
      if (run.underline) html = `<u>${html}</u>`;
      if (run.italic) html = `<i>${html}</i>`;
      if (run.bold) html = `<b>${html}</b>`;
      return html;
    })
    .join('');

/** `rgb(0, 255, 255)` (what the DOM reports) → `#00ffff`. */
const cssColorToHex = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (isColorValue(trimmed)) return trimmed.toLowerCase();
  const rgb = trimmed.match(/^rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
  if (!rgb) return undefined;
  return `#${rgb.slice(1, 4).map((part) => Number(part).toString(16).padStart(2, '0')).join('')}`;
};

/**
 * HTML → markup. Walks the tree accumulating marks, so it does not matter
 * which shape the browser's execCommand left behind — `<b>` or
 * `style="font-weight: bold"`, `<font color>` or a styled span, spans nested
 * three deep — every text node is emitted with the marks in force above it,
 * and `serializeRuns` writes them out canonically.
 */
export const htmlToMarkup = (root: HTMLElement): string => {
  const runs: TextRun[] = [];

  const walk = (node: Node, marks: TextRun) => {
    if (node.nodeType === Node.TEXT_NODE) {
      // The field is one line: a stray newline from a paste is just a space
      const text = (node.textContent || '').replace(/ /g, ' ').replace(/[\r\n]+/g, ' ');
      if (text) runs.push({ ...marks, text });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node as HTMLElement;
    const next: TextRun = { ...marks };
    const tag = element.tagName;
    if (tag === 'BR') {
      runs.push({ ...marks, text: ' ' });
      return;
    }
    if (tag === 'B' || tag === 'STRONG') next.bold = true;
    if (tag === 'I' || tag === 'EM') next.italic = true;
    if (tag === 'U' || tag === 'INS') next.underline = true;

    const { fontWeight, fontStyle, textDecorationLine, textDecoration, color } = element.style;
    if (fontWeight === 'bold' || fontWeight === 'bolder' || Number(fontWeight) >= 600) next.bold = true;
    if (fontWeight === 'normal' || (fontWeight && Number(fontWeight) < 600)) next.bold = false;
    if (fontStyle === 'italic') next.italic = true;
    if (fontStyle === 'normal') next.italic = false;
    const decoration = `${textDecorationLine} ${textDecoration}`;
    if (decoration.includes('underline')) next.underline = true;
    else if (decoration.includes('none')) next.underline = false;
    const cssColor = cssColorToHex(color) || cssColorToHex(element.getAttribute('color') || '');
    if (cssColor) next.color = cssColor;

    element.childNodes.forEach((child) => walk(child, next));
  };

  root.childNodes.forEach((child) => walk(child, { text: '' }));
  return serializeRuns(runs);
};
